import React from "react";
import { connect } from "react-redux";
import language from "../../language/index.js";
import { Table } from "../Table/index.js";
import { CSVLink } from "react-csv";
import bloodUsageQuery, {
  deleteBloodUsage,
  getBloodUsageDashboardCounts,
} from "../../utils/queries/bloodUsageQuery.js";
import exportPDF from "../../utils/exportPDF.js";
import NewUsage from "./NewUsage.js";
import { DeleteModal, Modal } from "../Modal/index.js";
import { defineRole } from "../../utils/handleAccessRoles.js";
import { getStorage } from "../../utils/storage.js";
import RecipientInfo from "../BloodRecipient/RecipientInfo.js";
import { ROLE_BLOOD_USAGE } from "../../constants/ROLES.js";
import { fetchRejectionStatus } from "../../utils/queries/rejectionStatus.js";
import PrintableLabel from "../Production/ComponentLabeling/printableLabel/index.js";
import icons from "../../constants/icons.js";
import CardCount from "../CardCount/CardCount.js";
import FollowUpModal from "./FollowUpModal.js";

// eslint-disable-next-line no-unused-vars
let copyData = [];
// eslint-disable-next-line no-unused-vars

class BloodUsage extends React.Component {
  csvDownload = React.createRef();

  state = {
    isLoading: false,
    data: [],
    page: 1,
    limit: 10,
    search_text: "",
    selectedData: {},
    delete_value: "",
    error: {},
    csvData: [],
    user: {},
    isLoadingCounts: false,
    isLoadingReactions: false,
    counts: {
      total: 0,
      transfusionPending: 0,
      transfused: 0,
      untransfused: 0,
      withReaction: 0,
      withoutReaction: 0,
    },
    reactionOptions: [],
    selectedReaction: null,
    filters: {},
    reportListData: [],
    reportListPage: 1,
    reportListLimit: 10,
    reportListSearchText: "",
    reportListTotalPageCount: 0,
    reportListIsLoading: false,
    reportListSearching: false,
    reportListSelectedReaction: null,
    reportListFilters: {},
    reportListTitle: "",
    showFollowUpModal: false,
    followUpBloodUsageId: null,
  };

  componentDidMount = async () => {
    const user = await getStorage();
    const donationNumber = (this.props.donationNumber || "").trim();
    const reactionOptions = await this.getReactionOptions();
    const hospitalFilter = user.hospital
      ? { hospital: user.hospital?._id || user.hospital }
      : {};

    this.setState({
      user,
      reactionOptions,
      search_text: donationNumber || "",
      filters: { ...this.state.filters, ...hospitalFilter },
    });
    await this.getData(
      true,
      donationNumber !== "" ? { q: donationNumber, ...hospitalFilter } : hospitalFilter,
    );
    if (this.props.showDashboardCounts || this.props.showDashboardCountsOnly) {
      await this.getDashboardCounts(
        true,
        donationNumber !== "" ? { q: donationNumber, ...hospitalFilter } : hospitalFilter,
      );
    }
  };

  onChangeText(field, e) {
    let { error } = this.state;

    delete error[field];

    this.setState({
      [field]: e?.target?.value,
      error,
    });
  }

  fetchListData = async (isLoading, search = {}, target = "main") => {
    try {
      const { defaultLanguage } = this.props;
      const page =
        target === "report" ? this.state.reportListPage : this.state.page;
      const limit =
        target === "report" ? this.state.reportListLimit : this.state.limit;
      const currentFilters =
        target === "report" ? this.state.reportListFilters : this.state.filters;
      const filters = {
        ...currentFilters,
        ...search,
      };

      this.setState(
        target === "report"
          ? { reportListIsLoading: isLoading }
          : { isLoading },
      );

      const status = this.props.isForPending
        ? ["TRANSFUSION_PENDING", "RESERVATION_CANCELLED"]
        : ["untransfused", "transfused"];

      const { data, count, totalRecords } = await bloodUsageQuery(
        defaultLanguage,
        {
          page,
          limit,
          status,
          ...filters,
        },
      );
      this.setState(
        target === "report"
          ? {
              reportListData: data,
              reportListTotalPageCount: count,
              reportListTotalRecords: totalRecords,
              reportListIsLoading: false,
              reportListFilters: filters,
            }
          : {
              data,
              totalPageCount: count,
              totalRecords,
              isLoading: false,
              filters,
            },
      );

      if (target === "main") {
        copyData = data.slice(0);
      }
    } catch (error) {
      this.setState(
        target === "report"
          ? {
              reportListIsLoading: false,
            }
          : {
              isLoading: false,
            },
      );
    }
  };

  getData = async (isLoading, search = {}) => {
    await this.fetchListData(isLoading, search, "main");
  };

  getReportListData = async (isLoading, search = {}) => {
    await this.fetchListData(isLoading, search, "report");
  };

  getDashboardCounts = async (isLoadingCounts, search = {}) => {
    try {
      const { defaultLanguage } = this.props;

      this.setState({ isLoadingCounts });

      const counts = await getBloodUsageDashboardCounts(
        defaultLanguage,
        search,
      );

      this.setState({
        counts: {
          total: counts?.total || 0,
          transfusionPending: counts?.transfusionPending || 0,
          transfused: counts?.transfused || 0,
          untransfused: counts?.untransfused || 0,
        },
        isLoadingCounts: false,
      });
    } catch (error) {
      this.setState({
        isLoadingCounts: false,
      });
    }
  };

  getReactionOptions = async () => {
    try {
      this.setState({ isLoadingReactions: true });

      const { data = [] } = await fetchRejectionStatus(
        this.props.defaultLanguage,
        {
          type: "TRANSFUSION_REACTION",
          page: 1,
          limit: 100,
        },
      );

      const reactionOptions = data.map((reaction) => ({
        ...reaction,
        label: reaction.code
          ? `${reaction.code} ${reaction.label}`
          : reaction.label,
        value: reaction._id,
      }));

      this.setState({ isLoadingReactions: false });

      return reactionOptions;
    } catch (error) {
      this.setState({ isLoadingReactions: false });
      return [];
    }
  };

  handlePagination(page, _tab, limit) {
    this.setState(
      {
        page,
        ...(limit != null && { limit }),
      },
      () => {
        this.getData(true, {
          ...this.state.filters,
          page,
        });
      },
    );
  }

  handleLimitChange = (newLimit) => {
    this.setState({ limit: newLimit, page: 1 }, () => {
      this.getData(true, {
        ...this.state.filters,
        page: 1,
      });
    });
  };

  handleReportListPagination = (page) => {
    this.setState(
      {
        reportListPage: page,
      },
      () => {
        this.getReportListData(true, {
          ...this.state.reportListFilters,
          page,
        });
      },
    );
  };

  applyListFilter = async (search = {}) => {
    await this.getData(true, {
      ...search,
      page: 1,
    });
  };

  openReportListModal = async (title, search = {}) => {
    await this.setState({
      showReportListModal: true,
      reportListTitle: title,
      reportListPage: 1,
      reportListSearchText: "",
      reportListSelectedReaction: null,
      reportListFilters: {},
    });

    await this.getReportListData(true, {
      ...search,
      page: 1,
    });

    this.setState({
      reportListTitle: title,
    });
  };

  handleCountCardPress = (title, search = {}) => {
    if (this.props.showDashboardCountsOnly) return undefined;

    if (
      this.props.showDashboardCounts ||
      this.props.showDashboardCountsInModal
    ) {
      return this.openReportListModal(title, search);
    }

    return this.applyListFilter(search);
  };

  handleShowModal(modal, modalTitle, selectedData = {}) {
    this.setState({
      [modal]: true,
      modalTitle: modalTitle,
      selectedData,
    });
  }

  handleCloseModal(modal) {
    this.setState({
      [modal]: false,
    });
  }

  returnTableHeaders() {
    let headers = [
      {
        title: language[this.props.defaultLanguage].product,
        key: "inventory.code",
      },
      {
        title: language[this.props.defaultLanguage].donation,
        key: "inventory.donation.donationNumber",
        type: "link",
        onPress: (item) => {
          if (!!this.props.isForPending) {
            this.handleShowModal("showModal", item.name, item);
          } else {
            this.handleShowModal(
              "showLabelModal",
              item?.donor?.donorNumber,
              item,
            );
          }
        },
      },
      {
        title: language[this.props.defaultLanguage].patient_id,
        key: "patient.patientId",
      },
      {
        title: language[this.props.defaultLanguage].request_no || "Request No",
        key: "requestNo",
        type: "link",
        onPress: (item) => {
          if (item.requestNo) {
            window.location.href = `/dashboard/request/${item.requestNo}`;
          }
        },
      },
      {
        title: language[this.props.defaultLanguage].status,
        key: "status",
        type: "status",
      },
      {
        title: language[this.props.defaultLanguage].hospital,
        key: "hospital.name",
        styles: {
          textTransform: "uppercase",
        },
      },
      {
        title: language[this.props.defaultLanguage].BBMS_NUMBER,
        key: "patient.patientBBMSNumber",
        styles: {
          textTransform: "uppercase",
        },
      },
      {
        title: language[this.props.defaultLanguage].blood_group,
        key: "bloodGroup",
        styles: {
          textTransform: "uppercase",
        },
      },
      {
        title: language[this.props.defaultLanguage].doctor,
        key: "doctor.name",
        styles: {
          textTransform: "uppercase",
        },
      },
      {
        title: language[this.props.defaultLanguage].reaction,
        key: "reaction.label",
        styles: {
          maxWidth: "180px",
        },
      },
      {
        title: language[this.props.defaultLanguage].usage_date,
        key: "usageDate",
        isMoment: true,
        formatTime: "lll",
      },
      {
        title: language[this.props.defaultLanguage].actions,
        key: "action",
      },
    ];

    return headers;
  }

  downloadExcel = async () => {
    try {
      this.setState({
        isLoading: true,
      });

      const { page, limit } = this.state;
      const { defaultLanguage } = this.props;

      const { data } = await bloodUsageQuery(defaultLanguage, {
        page,
        limit,
      });

      this.setState({
        isLoading: false,
        csvData: data,
      });

      this.csvDownload.current?.link.click();
    } catch (error) {
      this.setState({
        isLoading: false,
      });
    }
  };

  downloadPDF = async () => {
    this.setState({
      isLoading: true,
    });

    const { page, limit } = this.state;
    const { defaultLanguage } = this.props;

    const headers = this.returnTableHeaders();

    const { data } = await bloodUsageQuery(defaultLanguage, {
      page,
      limit,
    });

    this.setState({
      isLoading: false,
    });

    exportPDF("Blood Usage", headers, data);
  };

  onDelete = async () => {
    try {
      this.setState({ isDeleting: true });

      await deleteBloodUsage(
        this.props.defaultLanguage,
        this.state.selectedData._id,
      );

      this.setState({ isDeleting: false });

      this.getData(true);
      this.handleCloseModal("showDeleteModal");
    } catch (error) {
      console.log(error);
      this.setState({
        isDeleting: false,
      });
    }
  };

  handleRemoteSearch = async (search, target = "main") => {
    this.setState(
      target === "report"
        ? {
            reportListSearching: true,
          }
        : {
            searching: true,
          },
    );

    const params = {
      q: search || search !== "" ? search : undefined,
    };

    if (target === "report") {
      await this.getReportListData(true, {
        ...params,
        page: 1,
      });
    } else {
      await this.getData(true, params);
    }

    if (
      target === "main" &&
      (this.props.showDashboardCounts || this.props.showDashboardCountsOnly)
    ) {
      await this.getDashboardCounts(true, params);
    }

    this.setState(
      target === "report"
        ? {
            reportListSearching: false,
          }
        : {
            searching: false,
          },
    );
  };

  handleSearch = async (e) => {
    const search = e?.target?.value;
    this.setState({ search_text: search });
  };

  handleReportListSearch = async (e) => {
    const search = e?.target?.value;
    this.setState({ reportListSearchText: search });
  };

  handleReactionFilterChange = async (selectedReaction) => {
    this.setState({ selectedReaction });

    await this.applyListFilter({
      reaction: selectedReaction?.value || undefined,
    });
  };

  handleReportListReactionFilterChange = async (selectedReaction) => {
    this.setState({ reportListSelectedReaction: selectedReaction });

    await this.getReportListData(true, {
      reaction: selectedReaction?.value || undefined,
      page: 1,
    });
  };

  canEditUsage = (item = {}) =>
    defineRole({
      roles: this.state.user.accessRole,
      menu: ROLE_BLOOD_USAGE,
      operation: "update",
    }) && item?.status?.toUpperCase() === "UNTRANSFUSED";

  shouldShowReactionFilter = (mode = "main") => {
    if (this.props.isForPending) return false;

    if (mode === "report") {
      return this.state.reportListFilters?.status !== "TRANSFUSION_PENDING";
    }

    return true;
  };

  handleOpenFollowUp = (item) => {
    this.setState({
      showFollowUpModal: true,
      followUpBloodUsageId: item._id,
    });
  };

  handleCloseFollowUp = () => {
    this.setState({
      showFollowUpModal: false,
      followUpBloodUsageId: null,
    });
  };

  getTableActions = () => [
    {
      canAccess: true,
      name: language[this.props.defaultLanguage].edit,
      icon: icons.edit,
      onPress: (item) =>
        this.canEditUsage(item)
          ? this.handleShowModal(
              "showModal",
              language[this.props.defaultLanguage].edit,
              item,
            )
          : null,
      isHidden: (item) => !this.canEditUsage(item),
    },
    {
      canAccess: true,
      icon: icons.chat,
      name: "Follow Up",
      onPress: (item) => this.handleOpenFollowUp(item),
      isHidden: (item) => !item.reaction,
    },
    {
      canAccess: true,
      icon: this.props.isForPending ? icons.edit : icons.view,
      name: this.props.isForPending
        ? language[this.props.defaultLanguage].update
        : language[this.props.defaultLanguage].view,
      onPress: this.props.isForPending
        ? (item) =>
            this.handleShowModal(
              "showModal",
              language[this.props.defaultLanguage].update,
              item,
            )
        : (item) =>
            this.handleShowModal(
              "showLabelModal",
              language[this.props.defaultLanguage].label,
              item,
            ),
    },
    {
      canAccess: true,
      icon: icons.delete,
      name: language[this.props.defaultLanguage].delete,
      onPress: (item) =>
        this.handleShowModal(
          "showDeleteModal",
          language[this.props.defaultLanguage].delete,
          item,
        ),
    },
  ];

  renderUsageTable = (mode = "main") => (
    <Table
      data={mode === "report" ? this.state.reportListData : this.state.data}
      isSearch
      page={mode === "report" ? this.state.reportListPage : this.state.page}
      limit={mode === "report" ? this.state.reportListLimit : this.state.limit}
      search_text={
        mode === "report"
          ? this.state.reportListSearchText
          : this.state.search_text
      }
      addSearchButton
      searching={
        mode === "report"
          ? this.state.reportListSearching
          : this.state.searching
      }
      handleSearch={
        mode === "report"
          ? this.handleReportListSearch.bind(this)
          : this.handleSearch.bind(this)
      }
      handlePressSearch={() =>
        mode === "report"
          ? this.handleRemoteSearch(this.state.reportListSearchText, "report")
          : this.handleRemoteSearch(this.state.search_text, "main")
      }
      totalPageCount={
        mode === "report"
          ? this.state.reportListTotalPageCount
          : this.state.totalPageCount
      }
      totalRecords={
        mode === "report"
          ? this.state.reportListTotalRecords
          : this.state.totalRecords
      }
      isLoading={
        mode === "report"
          ? this.state.reportListIsLoading
          : this.state.isLoading
      }
      handlePagination={
        mode === "report"
          ? this.handleReportListPagination.bind(this)
          : this.handlePagination.bind(this)
      }
      onLimitChange={mode !== "report" ? this.handleLimitChange : undefined}
      headers={this.returnTableHeaders()}
      actions={this.getTableActions()}
      moreFilters={
        !this.shouldShowReactionFilter(mode)
          ? []
          : [
              {
                inputType: "selection",
                placeholder: language[this.props.defaultLanguage].reaction,
                value:
                  mode === "report"
                    ? this.state.reportListSelectedReaction
                    : this.state.selectedReaction,
                onChange:
                  mode === "report"
                    ? this.handleReportListReactionFilterChange
                    : this.handleReactionFilterChange,
                options: this.state.reactionOptions,
                isLoading: this.state.isLoadingReactions,
              },
            ]
      }
    />
  );

  render() {
    const showCounts =
      this.props.showDashboardCounts || this.props.showDashboardCountsOnly;
    const showInlineList =
      !this.props.showDashboardCountsOnly &&
      !this.props.showDashboardCountsInModal;

    return (
      <>
        {showCounts && (
          <div className="row mb-3">
            <div className="col-md-3">
              <CardCount
                count={this.state.counts.total}
                title="Total usage"
                color="#078ECE"
                icon="bloodtype"
                isLoading={this.state.isLoadingCounts}
                onPress={
                  this.props.showDashboardCountsOnly
                    ? undefined
                    : () =>
                        this.handleCountCardPress("Total usage", {
                          status: undefined,
                          reaction: undefined,
                          hasReaction: undefined,
                        })
                }
              />
            </div>
            <div className="col-md-3">
              <CardCount
                count={this.state.counts.transfusionPending}
                title="Pending transfusion"
                color="orange"
                icon="schedule"
                isLoading={this.state.isLoadingCounts}
                onPress={
                  this.props.showDashboardCountsOnly
                    ? undefined
                    : () =>
                        this.handleCountCardPress("Pending transfusion", {
                          status: "TRANSFUSION_PENDING",
                          reaction: undefined,
                          hasReaction: undefined,
                        })
                }
              />
            </div>
            <div className="col-md-3">
              <CardCount
                count={this.state.counts.transfused}
                title="Transfused"
                color="green"
                icon="check"
                isLoading={this.state.isLoadingCounts}
                onPress={
                  this.props.showDashboardCountsOnly
                    ? undefined
                    : () =>
                        this.handleCountCardPress("Transfused", {
                          status: "transfused",
                          reaction: undefined,
                          hasReaction: undefined,
                        })
                }
              />
            </div>
            <div className="col-md-3">
              <CardCount
                count={this.state.counts.untransfused}
                title="Untransfused"
                color="#6c757d"
                icon="close"
                isLoading={this.state.isLoadingCounts}
                onPress={
                  this.props.showDashboardCountsOnly
                    ? undefined
                    : () =>
                        this.handleCountCardPress("Untransfused", {
                          status: "untransfused",
                          reaction: undefined,
                          hasReaction: undefined,
                        })
                }
              />
            </div>
          </div>
        )}
        {showInlineList && this.renderUsageTable()}
        {(this.props.showDashboardCounts ||
          this.props.showDashboardCountsInModal) && (
          <Modal
            handleClose={this.handleCloseModal.bind(
              this,
              "showReportListModal",
            )}
            show={this.state.showReportListModal}
            title={this.state.reportListTitle}
            size="xl"
          >
            <div className="card-body">{this.renderUsageTable("report")}</div>
          </Modal>
        )}
        {!this.props.showDashboardCountsOnly && (
          <Modal
            handleClose={this.handleCloseModal.bind(this, "showModal")}
            show={this.state.showModal}
            title={this.state.modalTitle}
            showHeaderBottomBorder={false}
          >
            <NewUsage
              handleCloseModal={this.handleCloseModal.bind(this, "showModal")}
              getData={this.getData.bind(this)}
              {...this.state.selectedData}
            />
          </Modal>
        )}
        {!this.props.showDashboardCountsOnly && (
          <Modal
            handleClose={this.handleCloseModal.bind(this, "showLabelModal")}
            show={this.state.showLabelModal}
            showHeaderBottomBorder={false}
          >
            <PrintableLabel
              handleCloseModal={this.handleCloseModal.bind(
                this,
                "showLabelModal",
              )}
              productId={this.state?.selectedData?.inventory?._id}
            />
          </Modal>
        )}
        {!this.props.showDashboardCountsOnly && (
          <Modal
            handleClose={this.handleCloseModal.bind(
              this,
              "showRecipientInfoModal",
            )}
            show={this.state.showRecipientInfoModal}
            title={this.state.modalTitle}
            showHeaderBottomBorder={false}
          >
            <div className="card-body">
              <RecipientInfo id={this.state?.selectedData?.patient?._id} />
            </div>
          </Modal>
        )}
        {!this.props.showDashboardCountsOnly && (
          <Modal
            handleClose={this.handleCloseModal.bind(this, "showDeleteModal")}
            show={this.state.showDeleteModal}
            title={this.state.modalTitle}
          >
            <DeleteModal
              handleCloseModal={this.handleCloseModal.bind(
                this,
                "showDeleteModal",
              )}
              error={this.state.error.delete_password}
              value={this.state.delete_value}
              onDelete={this.onDelete.bind(this)}
              onChange={(e) => this.onChangeText("delete_value", e)}
              isDeleting={this.state.isDeleting}
              name={this.state.selectedData.name}
              getData={this.getData.bind(this)}
            />
          </Modal>
        )}
        {!this.props.showDashboardCountsOnly && (
          <CSVLink
            ref={this.csvDownload}
            filename={"Blood Usage" + new Date().getTime()}
            data={this.state.csvData}
          ></CSVLink>
        )}
        {this.state.showFollowUpModal && (
          <FollowUpModal
            handleClose={this.handleCloseFollowUp}
            show={this.state.showFollowUpModal}
            bloodUsageId={this.state.followUpBloodUsageId}
            defaultLanguage={this.props.defaultLanguage}
          />
        )}
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return {
    defaultLanguage,
  };
};

export default connect(mapStateToProps)(BloodUsage);
